import { AsyncPipe } from '@angular/common';
import { Component, Input, OnChanges, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { OfferSource } from '@cds';
import { AutomationDirective } from '@frontend/sports/automation/core-feature';
import { EventModel, EventOption, EventOptionGroup } from '@frontend/sports/betting-offer/feature/model';
import { CustomActiveStateDirective } from '@frontend/sports/common/ui/custom-active-state';
import { HapticsDirective } from '@frontend/sports/haptic-feedback/feature';
import { NativePrice } from '@frontend/sports/odds/feature/native-price';
import { PickSourceProvider, SourceOfPick } from '@frontend/sports/option/pick/feature/tracking';
import { NOT_APPLICABLE, TRACKING_NO_GAMEDETAILS, trackingConstants } from '@frontend/sports/tracking/feature';
import { Store } from '@ngrx/store';

import { ExternalBetslipActions } from '../../betslip/core/external-betslip-actions';
import { V1PickId, V2OptionMarketPickId } from '../../betslip/core/picks/pick-id';
import { getBetslipGroupInformation } from '../bet-builder-core/utils';
import { BetslipUpsellConstants } from '../betslip-base/models/betslip-upsell.model';
import { StreamingLaunchDarklyTrackingService } from '../event-details-common/streaming-launch-darkly-tracking.service';
import { isSheetViewOpen } from '../ui-manager/ui-manager.state';
import { BetTrackingService } from './bet-tracking.service';
import { OptionIndicatorDirective } from './option-indicator.directive';
import { PickComponent } from './pick.component';

@Component({
    selector: 'ms-event-pick',
    templateUrl: './pick.component.html',
    imports: [CustomActiveStateDirective, OptionIndicatorDirective, AutomationDirective, AsyncPipe, HapticsDirective],
    styleUrl: './event-pick.component.scss',
})
export class EventPickComponent extends PickComponent implements OnChanges {
    private pickSourceProvider = inject(PickSourceProvider);
    private store = inject(Store);
    private readonly betTrackingService = inject(BetTrackingService);

    @Input() option: EventOption;
    @Input() optionGroup: EventOptionGroup;
    @Input() event: EventModel;
    @Input() isFromPortal?: boolean;
    @Input() isPriceBoosted?: boolean;
    @Input() calenderTrack?: string;
    @Input() trackingControllerTabName?: string;
    @Input() trackingPlayerName?: string;

    private isSheetViewOpen = false;
    override pickId: V1PickId | V2OptionMarketPickId;

    private streamingLaunchDarklyTrackingService = inject(StreamingLaunchDarklyTrackingService);

    constructor() {
        super();
        this.store
            .select(isSheetViewOpen)
            .pipe(takeUntilDestroyed())
            .subscribe((isSheetView: boolean) => (this.isSheetViewOpen = isSheetView));
    }

    private canTrackPickDetails(): boolean {
        return (
            this.trackingSource === BetslipUpsellConstants.TrackingSource ||
            this.sourceOfPick === SourceOfPick.OutrightsGrid ||
            this.sourceOfPick === SourceOfPick.BetStationMarquee
        );
    }

    get price(): NativePrice {
        return this.isPriceBoosted ? this.option.boostedPrice! : this.option.nativePrice;
    }

    get priceId(): number | undefined {
        return this.isPriceBoosted ? this.option.boostedPriceId! : this.option.id;
    }

    private createPickId(): V1PickId | V2OptionMarketPickId {
        const isHybridSplitTv1Pick = this.event.offerSource === OfferSource.V2 && this.optionGroup.source === OfferSource.V1;
        const isPureTv1Pick = this.event.offerSource === OfferSource.V1 && this.optionGroup.source !== OfferSource.V2;
        if (isPureTv1Pick || isHybridSplitTv1Pick) {
            return new V1PickId(this.option.originalEventId || this.event.id, this.option.optionGroupId, this.option.id);
        } else {
            const priceId = this.isPriceBoosted ? this.option.boostedPriceId! : this.option.priceId!;

            return new V2OptionMarketPickId(
                this.event.hybridFixtureData != null ? this.event.hybridFixtureData.fixtureId : this.option.originalEventId || this.event.id,
                this.option.optionGroupId,
                this.option.id,
                priceId,
            );
        }
    }

    protected addPick(): void {
        const pickSource = this.pickSource;
        this.store.dispatch(
            ExternalBetslipActions.addPick({
                pickId: this.pickId,
                tracking: pickSource,
                isPriceBoosted: this.isPriceBoosted,
                groupInfo: getBetslipGroupInformation(this.event),
                isBetBuilder: this.option.isBetBuilder,
            }),
        );
        this.streamingLaunchDarklyTrackingService.trackAddPickClickInEDPPage(pickSource?.sourceOfPick);
    }

    protected removePick(): void {
        this.store.dispatch(ExternalBetslipActions.removePick({ pickId: this.pickId }));
    }

    override pickClick(): void {
        super.pickClick();
    }

    ngOnChanges(): void {
        this.state.locked = !this.option.online || !this.optionGroup.online;
        this.setPickId(this.createPickId());
    }

    private get pickSource() {
        const pickSource = this.pickSourceProvider.get(
            this.trackingSource,
            this.trackingPosition,
            this.isFallbackMarketEnabled,
            this.marqueeName,
            this.sitecoreTemplateId,
            this.marqueeType,
            undefined,
            this.isAutomatedMarquee,
        );

        const betDetails = this.betTrackingService.getBetDetails(
            this.optionGroup.hasBetBuilderMarkets,
            this.event.betBuilderInfo,
            this.sourceOfPick,
            this.betDetails,
        );

        pickSource.additional = pickSource.additional || {};
        pickSource.betDetails = pickSource.betDetails ? pickSource.betDetails + ';' + betDetails : betDetails;

        if (this.canTrackPickDetails()) {
            pickSource.additional[trackingConstants.SPORT_ID] = this.event.sport.id.toString();
            pickSource.additional[trackingConstants.SPORT_LEAGUE_ID] = this.event.league.id.toString();
            pickSource.additional[trackingConstants.RESULT_ID] = this.option.id.toString();
            pickSource.additional[trackingConstants.MARKET_ID] = this.option.optionGroupId.toString();
            pickSource.additional[trackingConstants.MARQUEE_CONTENT_LOGIC] = this.isFallbackMarketEnabled ? 'fallback' : 'default';
        }

        if (this.isSheetViewOpen) {
            pickSource.additional[trackingConstants.COMPONENT_MODULE_NAME] = trackingConstants.SEE_ALL_OVERLAY;
            pickSource.sheetviewSuffix = `/${trackingConstants.SEE_ALL_OVERLAY}`;
            pickSource.source = this.trackingSource;
        }

        if (this.trackingControllerTabName) {
            pickSource.controllerTabName = this.trackingControllerTabName;
        }
        if (this.trackingPlayerName) {
            pickSource.playerName = this.trackingPlayerName;
        }
        if (this.tracking?.variant) {
            pickSource.variant = this.tracking.variant;
        }
        if (this.tracking?.betPickType) {
            pickSource.betPickType = this.tracking.betPickType;
        }
        pickSource.gameDetails = this.trackingGameDetails ?? TRACKING_NO_GAMEDETAILS;
        pickSource.sourceOfPick = this.sourceOfPick ?? NOT_APPLICABLE;
        pickSource.betType = this.optionGroup.name;
        pickSource.contentPosition = this.trackingPosition?.toString();
        pickSource.moduleType = this.trackingModuleType;
        pickSource.additional[trackingConstants.BET_CALENDER] = this.calenderTrack || NOT_APPLICABLE;
        pickSource.parentLeagueId = this.event.league.parentLeagueId;

        return pickSource;
    }
}
